import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../api/invoicesApi';
import { useToast } from '../../../shared/components/toast/useToast';
import type { CreateInvoicePayload, CreateBackdatedReceiptPayload, SendInvoiceToCustomerPayload } from '../types';
import { useAuthStore } from '../../auth/store';

const errMsg = (err: unknown, fallback: string) =>
  ((err as Error)?.message) || fallback;

export const useInvoices = (params?: { tenantId?: string; status?: string; page?: number; limit?: number }) => {
  const selectedTenantId = useAuthStore((state) => state.selectedTenantId);
  return useQuery({
    queryKey: ['invoices', selectedTenantId, params],
    queryFn: () => invoicesApi.list(params),
    staleTime: 60 * 1000,
  });
};

export const useInvoice = (id: string) =>
  useQuery({
    queryKey: ['invoices', id],
    queryFn: () => invoicesApi.get(id),
    staleTime: 60 * 1000,
    enabled: !!id,
  });

export const useOverdueInvoices = () =>
  useQuery({
    queryKey: ['invoices', 'overdue'],
    queryFn: invoicesApi.getOverdue,
    staleTime: 60 * 1000,
  });

export const useCreateInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateInvoicePayload) => invoicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Invoice created successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to create invoice')),
  });
};

export const useCreateBackdatedReceipt = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (data: CreateBackdatedReceiptPayload) => invoicesApi.createBackdatedReceipt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success('Receipt issued successfully');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to issue receipt')),
  });
};

export const useReissueInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.reissue(id),
    onSuccess: (reissued) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast.success(`Reissued as ${reissued.invoiceNumber}`);
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to reissue invoice')),
  });
};

export const useSendInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.send(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice sent to tenant');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to send invoice')),
  });
};

export const useMarkPaidInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.markPaid(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice marked as paid');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to mark invoice as paid')),
  });
};

export const useVoidInvoice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.void(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice voided');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to void invoice')),
  });
};

export const useSendInvoiceToCustomer = (id: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (payload: SendInvoiceToCustomerPayload) => invoicesApi.sendToCustomer(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoices', id] });
      toast.success('Invoice sent to customer');
    },
    onError: (err: unknown) => toast.error(errMsg(err, 'Failed to send invoice to customer')),
  });
};

/** Debounced-friendly customer search for the invoice recipient picker. */
export const useCustomerSearch = (merchantId: string | undefined, search: string) =>
  useQuery({
    queryKey: ['invoice-customers', merchantId, search],
    queryFn: () => invoicesApi.searchCustomers(merchantId as string, search),
    enabled: !!merchantId,
    staleTime: 30 * 1000,
  });
